import OTPInput from "components/auth/OTPInput";
import GenericButton from "components/common/buttons/GenericButton";
import { OTPChannel } from "lib/types/user";
import React, { useEffect, useMemo, useRef, useState } from "react";
import smsLogo from "assests/images/sms-otp.svg";
import { useLocale } from "lib/hooks/useLocale";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import { Lock } from "@phosphor-icons/react";
import ShopfloLogo from "assests/images/flo-full-logo-mono.svg";
import whatsappLogo from "assests/images/whatsapp-otp.svg";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import MarketingConsent from "components/common/MarketingConsent";
import { createGuestDraftOrder } from "lib/utils/auth";
import { redirectUrl } from "lib/utils/helpers";
import { errorToast } from "lib/utils/toasters";

interface OTPFormProps {
  isLoading: boolean;
  otpLength: number;
  otpValue: string;
  handleSubmit: any;
  handleChange: (a: string) => void;
  validateOtp: (otp: string) => Promise<void>;
  handleResendOtp: (channel: OTPChannel) => Promise<void>;
  otpCounter: number;
  otpCounterExpired: boolean;
  error?: boolean;
  errorMessage?: string;
  errorCode?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  headerComponent?: React.ReactNode;
  emailValidation: boolean;
  showLoginConsent?: boolean;
  context?: "cod" | "auth" | "sso" | "cart";
  showSecuredByShopflo?: boolean;
  disableResend?: boolean;
  checkoutId?: string;
  guestPhoneNumber?: string;
}

const OTPVERIFICATIONERRORCODES = [
  "GE_001",
  "AUTH_016",
  "AUTH_017",
  "AUTH_018",
  "AUTH_021",
  "AUTH_023",
  "AUTH_024",
  "AUTH_025",
  "AUTH_026",
  "AUTH_027",
];

const OTPForm: React.FC<OTPFormProps> = ({
  handleSubmit,
  otpValue,
  handleChange,
  validateOtp,
  handleResendOtp,
  error,
  errorCode,
  errorMessage,
  isLoading,
  otpLength,
  disabled = false,
  autoFocus = true,
  otpCounter,
  otpCounterExpired,
  headerComponent,
  emailValidation = false,
  showLoginConsent = false,
  context = "auth",
  showSecuredByShopflo = false,
  disableResend = false,
  checkoutId,
  guestPhoneNumber,
}) => {
  const { t } = useLocale();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    state: { merchant, cartMetadata, checkoutMetadata },
  } = useMerchantContext();

  const [loginConsent, setLoginConsent] = useState(true);
  const isAuth = context === "auth" || context === "sso";
  const isCart = context === "cart";
  const isSso = context === "sso";

  const otpConfig = isCart
    ? cartMetadata?.uiAttributes?.otpConfig
    : checkoutMetadata?.uiAttributes?.otpConfig;
  const whatsappOtpEnabled = Boolean(otpConfig?.whatsappOtpEnabled);
  const guestCheckoutEnabled = Boolean(otpConfig?.guestCheckoutEnabled);

  const [resendCount, setResendCount] = useState(0);
  const [isContinuing, setIsContinuing] = useState(false);

  const onResend = (channel: OTPChannel) => {
    setResendCount((count) => count + 1);
    handleResendOtp(channel);
  };

  const showContinueWithoutVerifying =
    (isCart || context === "auth") &&
    guestCheckoutEnabled &&
    resendCount >= 1 &&
    Boolean(checkoutId) &&
    Boolean(guestPhoneNumber);

  const handleContinueWithoutVerifying = async () => {
    if (!checkoutId || !guestPhoneNumber) return;
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_OTP_FALLBACK_GUEST_CHECKOUT,
      eventType: "click",
    });
    setIsContinuing(true);
    try {
      const invoiceUrl = await createGuestDraftOrder(checkoutId, guestPhoneNumber);
      if (!invoiceUrl) throw new Error("Missing invoice_url in draft-order response");
      redirectUrl(invoiceUrl);
    } catch (error) {
      setIsContinuing(false);
      errorToast(t("something_went_wrong"));
      console.error(error);
    }
  };

  const otpChannel = emailValidation ? "EMAIL" : "SMS";
  const failedAttemptsRef = useRef(0);

  useEffect(() => {
    if (error) {
      // Otp verification failed checks
      const hasOtpVerificationError = OTPVERIFICATIONERRORCODES.includes(errorCode ?? "");
      failedAttemptsRef.current += 1;
      if (hasOtpVerificationError) {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_OTP_VERIFICATION_FAILED,
          eventType: "flo_action",
          metaData: {
            otpData: {
              channel: otpChannel,
              failureReason: errorMessage,
              attemptNumber: failedAttemptsRef.current,
            },
          },
        });
      }

      // Base login_failed event
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_LOGIN_FAILED,
        eventType: "flo_action",
        metaData: {
          otpData: {
            channel: otpChannel,
            failureReason: errorMessage,
            attemptNumber: failedAttemptsRef.current,
          },
        },
      });
    }
  }, [error]);

  return (
    <>
      {headerComponent}
      <form onSubmit={() => handleSubmit(validateOtp)} className="flex w-full flex-col items-center ">
        <OTPInput
          OTPLength={4}
          disabled={disabled || disableResend}
          value={otpValue}
          onChange={handleChange}
          hasError={(error && !isLoading) || false}
          autoFocus={autoFocus}
          inputType={"tel"}
          disableTheme={context === "sso"}
          variant={context === "sso" ? "solidLight" : "default"}
        />

        {error && !isLoading ? (
          <p className={`${showLoginConsent && !isCart ? "my-3" : "mt-3"} text-text-xs-regular text-ouch`}>
            {errorMessage}
          </p>
        ) : !error && showLoginConsent && !isCart ? (
          <p className={`my-3 text-text-xs-regular text-coal-light`}>{t("otp_incorrect_login")}</p>
        ) : !error && !showLoginConsent && context === "cod" ? (
          <p className={`mt-3 text-text-xs-regular text-coal-light`}>{t("enter_otp_to_place_order")}</p>
        ) : null}
        {showLoginConsent && (
          <div
            className="flex cursor-pointer items-center gap-1 text-xs font-medium text-coal-light"
            onClick={() => {
              sendAnalyticsEvent({
                eventName: loginConsent
                  ? analyticsEvents.FLO_COOKIE_LOGIN_DISABLED
                  : analyticsEvents.FLO_COOKIE_LOGIN_ENABLED,
                eventType: "click",
              });
              setLoginConsent(!loginConsent);
            }}>
            <MarketingConsent />
          </div>
        )}

        <div className={`${context === "cod" ? "mt-2" : "mt-4"} mb-2 flex w-full flex-col items-center`}>
          {
            <div className={context !== "cod" ? "px-3 pb-3" : !otpCounterExpired ? "py-3" : ""}>
              <p className="text-center text-text-sm-medium text-coal-dark opacity-70">
                {otpCounter > 0 && !disableResend
                  ? `${t("resend_otp_timer")} 00:${otpCounter < 10 ? `0${otpCounter}` : otpCounter}`
                  : null}
                {otpCounterExpired && emailValidation ? "" : null}
                {otpCounterExpired && !emailValidation && (isAuth || isCart)
                  ? `${t("resend_otp_via")}`
                  : null}
              </p>
            </div>
          }
          {otpCounterExpired && (
            <>
              {emailValidation && (
                <>
                  <GenericButton
                    buttonText={t("resend_otp")}
                    isDisabled={disableResend}
                    className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-zinc-900/10 px-2 py-1.5 text-text-sm-semibold text-zinc-900/70 disabled:cursor-not-allowed disabled:border-zinc-900/5 disabled:text-zinc-900/40"
                    onClick={(e) => {
                      e.preventDefault();
                      onResend("EMAIL");
                    }}
                  />
                </>
              )}
              {!emailValidation && (isAuth || isCart) && (
                <div className="flex w-full justify-center gap-2">
                  <GenericButton
                    buttonText={t("send_via_sms")}
                    isDisabled={disableResend}
                    className="flex items-center justify-center gap-1 rounded-lg border border-zinc-900/10 px-4 py-1.5 text-text-sm-semibold text-zinc-900/70 disabled:cursor-not-allowed disabled:border-zinc-900/5 disabled:text-zinc-900/40"
                    iconComponent={<img src={smsLogo} alt="SMS Logo" />}
                    onClick={(e) => {
                      e.preventDefault();
                      onResend("SMS");
                    }}
                  />
                  {!isSso && whatsappOtpEnabled && (
                    <GenericButton
                      buttonText={t("send_via_whatsapp")}
                      isDisabled={disableResend}
                      className="flex items-center justify-center gap-1 rounded-lg border p-2 text-sm font-normal"
                      iconComponent={<img src={whatsappLogo} alt="Whatsapp Logo" />}
                      onClick={(e) => {
                        e.preventDefault();
                        onResend("WHATSAPP");
                      }}
                    />
                  )}
                </div>
              )}
              {!emailValidation && context === "cod" && (
                <div>
                  <GenericButton
                    buttonText={t("resend_otp")}
                    isDisabled={disableResend}
                    className="my-4 flex flex-1 items-center justify-center gap-1 rounded-lg border border-zinc-900/10 px-2 py-1.5 text-text-sm-semibold text-zinc-900/70 disabled:cursor-not-allowed disabled:border-zinc-900/5 disabled:text-zinc-900/40"
                    onClick={(e) => {
                      e.preventDefault();
                      onResend("SMS");
                    }}
                  />
                </div>
              )}
            </>
          )}
          {context === "cod" && (
            <p className="mb-4 mt-2 text-center text-xs text-coal-dark">{t("cod_otp_footer_message")}</p>
          )}
          {showContinueWithoutVerifying && (
            <button
              type="button"
              disabled={isContinuing}
              onClick={handleContinueWithoutVerifying}
              className="mt-3 text-text-sm-medium text-coal-dark opacity-70 underline decoration-dashed underline-offset-4 disabled:opacity-50">
              {t("skip_otp")}
            </button>
          )}
          {showSecuredByShopflo && (
            <div className="mt-2 flex flex-row items-center space-x-1">
              <Lock className="h-3 w-3 text-coal-light" strokeWidth={3} />
              <span className="text-[10px] font-normal text-coal-light">Secured by </span>
              <a href="https://shopflo.com" className="font-bold" target="_blank" rel="noreferrer">
                <img src={ShopfloLogo} className="h-3" alt={"shopflo-logo"} />
              </a>
            </div>
          )}
        </div>
      </form>
    </>
  );
};

export default OTPForm;
